<script lang="ts">
  import {slide} from 'svelte/transition'
  import LineGraphPrimitive from './LineGraphPrimitive.svelte'

  interface Props {
    dashboardState?: any
    segment?: string
    chartData?: any
  }

  let {
    dashboardState,
    segment,
    chartData: propChartData,
  }: Props = $props()

  const WEEKS_TO_SHOW = 15;

  type IndexedDate = {
    date: Date
    index: number
  };

  const getWeekStartMonday = (dateInput: string | Date) => {
    const date = new Date(dateInput);
    const day = date.getDay();
    const diffToMonday = day === 0 ? -6 : 1 - day;
    const monday = new Date(date);
    monday.setDate(date.getDate() + diffToMonday);
    monday.setHours(0, 0, 0, 0);
    return monday;
  };

  const formatWeekLabel = (dateInput: string | Date) => {
    const monday = getWeekStartMonday(dateInput);
    return `Week of ${monday.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    })}`;
  };

  const generateWeekLabels = () => {
    const labels = [];
    const today = new Date();

    for (let i = WEEKS_TO_SHOW - 1; i >= 0; i--) {
      const weekDate = new Date(today);
      weekDate.setDate(today.getDate() - (i * 7));
      labels.push(formatWeekLabel(weekDate));
    }

    return labels;
  };

  const formatDateLabels = (dateStrings: string[]) => {
    return dateStrings.map(dateStr => formatWeekLabel(dateStr));
  };
  
  // Options for the chart with UC Cincinnati branding
  const options = {
    responsive: true,
    plugins: {

      legend: {
        position: 'top',
        labels: {
          color: '#e00122', // UC Red for legend text
          font: {
            family: 'Open Sans, sans-serif',
            size: 14
          }
        }
      },
      title: {
        display: true,
        text: 'Accessibility Score over a 15-week period',
        color: '#e00122', // UC Red for title
        font: {
          family: 'Open Sans, sans-serif',
          size: 16,
          weight: 'bold'
        }
      },
    },
    scales: {
      x: {
        ticks: {
          color: '#333333', // Dark gray for x-axis labels
          font: {
            family: 'Open Sans, sans-serif',
            size: 12
          }
        },
        grid: {
          color: '#e0e0e0' // Light gray for grid lines
        }
      },
      y: {
        beginAtZero: true, // Force y-axis to start at 0
        min: 0, // Explicitly set minimum to 0
        max: 100, // Explicitly set maximum to 100
        ticks: {
          color: '#333333', // Dark gray for y-axis labels
          font: {
            family: 'Open Sans, sans-serif',
            size: 12
          },
          stepSize: 10, // Set tick intervals to 10
          callback: function(value: string | number) {
            return value.toString() + '%'; // Append percentage sign to y-axis labels
          }
        },
        grid: {
          color: '#e0e0e0' // Light gray for grid lines
        }
      }
    }
  };
  
  // Labels (last 15 weeks)
  const labels = generateWeekLabels()
  
  const applyDatasetStyle = (dataset: any) => ({
    ...dataset,
    borderColor: '#e00122', // UC Red
    backgroundColor: 'rgba(224, 1, 34, 0.1)', // UC Red with transparency
    pointBackgroundColor: '#e00122', // UC Red for data points
    pointBorderColor: '#ffffff', // White border for data points
    pointBorderWidth: 2,
    pointRadius: 4,
    pointHoverRadius: 6,
    pointHoverBackgroundColor: '#e00122',
    pointHoverBorderColor: '#ffffff',
    pointHoverBorderWidth: 2
  });

  const transformSIData = (siData: { SIOverallScore: string; SIRetrievalDate: string }[]) => {
    const today = new Date();
    const cutoffDate = getWeekStartMonday(new Date(today.getFullYear(), today.getMonth(), today.getDate() - ((WEEKS_TO_SHOW - 1) * 7)));

    const currentYear = today.getFullYear();

    const sorted = [...siData]
      .map(item => ({ date: getWeekStartMonday(item.SIRetrievalDate), score: parseFloat(item.SIOverallScore), raw: item.SIRetrievalDate }))
      .filter(item => !Number.isNaN(item.date.getTime()) && item.date >= cutoffDate && item.date.getFullYear() === currentYear)
      .sort((a, b) => a.date.getTime() - b.date.getTime() || new Date(a.raw).getTime() - new Date(b.raw).getTime());

    // Deduplicate by week (keep latest entry per week)
    const byWeek = new Map<string, typeof sorted[0]>();
    for (const item of sorted) {
      byWeek.set(item.date.toISOString(), item);
    }
    const deduped = Array.from(byWeek.values()).slice(-WEEKS_TO_SHOW);

    return {
      labels: deduped.map(item => formatWeekLabel(item.raw)),
      datasets: [applyDatasetStyle({
        label: 'Web Accessibility Score',
        data: deduped.map(item => item.score),
        tension: 0.1
      })]
    };
  };

  // Use prop data if available, otherwise fallback to sample data with UC branding colors
  const chartData = $derived(propChartData ? (() => {
    // Handle {data: [{SIOverallScore, SIRetrievalDate}]} format
    if (Array.isArray(propChartData.data) && propChartData.data[0]?.SIOverallScore !== undefined) {
      return transformSIData(propChartData.data);
    }

    if (Array.isArray(propChartData.labels) && typeof propChartData.labels[0] === 'string') {
      const today = new Date();
      const cutoffDate = getWeekStartMonday(new Date(today.getFullYear(), today.getMonth(), today.getDate() - ((WEEKS_TO_SHOW - 1) * 7)));

      const currentYear = today.getFullYear();

      const filteredIndices = propChartData.labels
        .map((dateStr: string, index: number): IndexedDate => ({ date: getWeekStartMonday(dateStr), index }))
        .filter(({ date }: IndexedDate) => !Number.isNaN(date.getTime()) && date >= cutoffDate && date.getFullYear() === currentYear)
        .map(({ index }: IndexedDate) => index)
        .slice(-WEEKS_TO_SHOW);

      return {
        ...propChartData,
        labels: formatDateLabels(filteredIndices.map((index: number) => propChartData.labels[index])),
        datasets: propChartData.datasets.map((dataset: any) => applyDatasetStyle({
          ...dataset,
          data: filteredIndices.map((index: number) => dataset.data[index])
        }))
      };
    }

    return {
      ...propChartData,
      datasets: propChartData.datasets.map(applyDatasetStyle)
    };
  })() : (() => {
    const siData = dashboardState?.graphData?.data;
    if (Array.isArray(siData) && siData[0]?.SIOverallScore !== undefined) {
      return transformSIData(siData);
    }
    return {
      labels,
      datasets: [
        {
          label: 'Web Accessibility Score',
          data: Array.isArray(dashboardState?.graphData) ? dashboardState.graphData : [65.5, 78.2, 52.8, 91.1, 43.7, 69.3, 82.4, 75.6],
          borderColor: '#e00122', // UC Red
          backgroundColor: 'rgba(224, 1, 34, 0.1)', // UC Red with transparency
          pointBackgroundColor: '#e00122', // UC Red for data points
          pointBorderColor: '#ffffff', // White border for data points
          pointBorderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 6,
          pointHoverBackgroundColor: '#e00122',
          pointHoverBorderColor: '#ffffff',
          pointHoverBorderWidth: 2,
          tension: 0.1 // Slight curve to the line
        },
      ],
    };
  })())

  let chartHasData = $derived(chartData.labels.length > 0)
</script>

{#if chartHasData}
  <div class="p-2 md:p-4" transition:slide>  
    <LineGraphPrimitive
    data={chartData}
    options={options}
    />
  </div>
{/if}